import React, { useState, useEffect, useMemo } from "react";
import { Helmet }     from "react-helmet-async";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ICourse }    from "../../Courses/CoursesPage";
import LoginModal     from "../../../components/Auth/LoginModel";
import TestPriceModal from "../../../components/Modals/TestPricingModal";
import "../CourseDetails.css";   // ← reuses ALL cd-* styles
import "./LiveClassDetails.css";
 
const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001/api/v1";
 
/* ── Date helpers ── */
function fmtFull(iso: string) {
  return new Date(iso).toLocaleString("en-IN", {
    weekday:"long", day:"numeric", month:"long", year:"numeric",
    hour:"2-digit", minute:"2-digit", hour12:true,
  });
}
function fmtShort(iso: string) {
  return new Date(iso).toLocaleString("en-IN", {
    weekday:"short", day:"numeric", month:"short",
    hour:"2-digit", minute:"2-digit", hour12:true,
  });
}
function timeUntil(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return "Starting now";
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (h > 48) return `in ${Math.floor(h/24)}d`;
  if (h > 0)  return `in ${h}h ${m}m`;
  return `in ${m}min`;
}
 
/* ── Countdown timer ── */
function Countdown({ target }: { target: string }) {
  const [diff, setDiff] = useState(0);
  useEffect(() => {
    const tick = () => setDiff(new Date(target).getTime() - Date.now());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
 
  if (diff <= 0) return <span className="lcd-starting-now">Starting now!</span>;
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
 
  return (
    <div className="lcd-countdown">
      {[{v:h,l:"Hrs"},{v:m,l:"Min"},{v:s,l:"Sec"}].map(u => (
        <div key={u.l} className="lcd-cd-unit">
          <span className="lcd-cd-val">{String(u.v).padStart(2,"0")}</span>
          <span className="lcd-cd-lbl">{u.l}</span>
        </div>
      ))}
    </div>
  );
}
 
/* ── Icons (all reused from CourseDetail, kept identical) ── */
const IcoStar      = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="#fbbf24" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>);
const IcoUsers     = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>);
const IcoClock     = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>);
const IcoCheck     = () => (<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>);
const IcoShield    = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>);
const IcoVideoFill = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="15" height="14" rx="2"/><path d="M17 9.5l5-3.5v13l-5-3.5"/></svg>);
const IcoAward     = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg>);
const IcoZap       = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>);
const IcoCalendar  = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>);
const IcoMic       = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>);
//const IcoLiveIcon  = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none"><rect x="2" y="7" width="20" height="15" rx="2"/><polyline points="17 2 12 7 7 2" stroke="currentColor" fill="none" strokeWidth="2"/></svg>);
 
/* Skeleton — reuses cd-skel-* classes */
const DetailSkeleton = () => (
  <div className="cd-page">
    <div className="cd-back-bar"><div className="cd-skel-line" style={{ width:140, height:16 }} /></div>
    <div className="cd-layout">
      <div className="cd-main">
        <div className="cd-skel-hero" />
        <div className="cd-skel-card" />
        <div className="cd-skel-card" />
      </div>
      <aside className="cd-sidebar"><div className="cd-skel-sidebar" /></aside>
    </div>
  </div>
);
 
/* ════════════════════════════════════════════
   COMPONENT
════════════════════════════════════════════ */
const LiveClassDetail: React.FC = () => {
  const { course_slug } = useParams<{ course_slug: string }>(); // slug
  const navigate    = useNavigate();
 
  const [lc,               setLc]               = useState<ICourse | null>(null);
  const [siblings,         setSiblings]         = useState<ICourse[]>([]);
  const [loading,          setLoading]          = useState(true);
  const [error,            setError]            = useState("");
  const [isEnrolled,       setIsEnrolled]       = useState(false);
  const [showLoginModal,   setShowLoginModal]   = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
 //console.log("COURSE_SLUG:", course_slug);
  /* ── Fetch this live class ── */
  useEffect(() => {
    if (!course_slug) return;
    setLoading(true);
    setError("");
    fetch(`${API_BASE}/courses/${course_slug}`)
      .then(r => r.json())
      .then(d => { if (d.success) setLc(d.data); else setError("Class not found"); })
      .catch(() => setError("Network error"))
      .finally(() => setLoading(false));
  }, [course_slug]);
 
  /* ── Fetch sibling sessions (same category, all live classes) ── */
  useEffect(() => {
    if (!course_slug) return;
    fetch(`${API_BASE}/live-classes/course/${course_slug}`)
      .then(r => r.json())
      .then(d => {
        if (d.success) setSiblings(d.data.filter((c: ICourse) => c.slug !== course_slug));
      })
      .catch(() => {});
  }, [ course_slug]);
 
  /* ── Check payment / enrollment status ── */
  useEffect(() => {
    if (!course_slug) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    fetch(`${API_BASE}/courses/${course_slug}/payment-status`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { if (d.success && d.data.paid) setIsEnrolled(true); })
      .catch(() => {});
  }, [course_slug]);
 
  /* ── After login success — re-check payment ── */
  const handleLoginSuccess = () => {
    if (!course_slug) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    fetch(`${API_BASE}/courses/${course_slug}/payment-status`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => {
        if (d.success && d.data.paid) setIsEnrolled(true);
        else setShowPricingModal(true);
      })
      .catch(() => {});
  };
 
  /* ── Enroll / Join click — same gate as CourseDetail / TestSeries ── */
  const handleEnrollClick = () => {
    if (!lc) return;
    // LIVE + enrolled → go to classroom immediately
    if (lc.status === "live" && isEnrolled) {
      navigate(`/live-classes/${lc.slug}/${lc._id}/join`);
      return;
    }
    // Ended + recording + enrolled → watch
    if (lc.status === "ended" && lc.recording_available && isEnrolled) {
      navigate(`/live-classes/${lc.slug}/${lc._id}/join`);
      return;
    }
    const token = localStorage.getItem("token");
    if (!token) {
      localStorage.setItem("postLoginModelRedirect", window.location.pathname);
      setShowLoginModal(true);
    } else {
      setShowPricingModal(true);
    }
  };
 //console.log("SIBLINGS:", siblings);
  /* ── Session row click ── */
  const handleSessionClick = (session: any) => {
    //console.log("SESSION:", session);
    if (!isEnrolled) { handleEnrollClick(); return; }
    if (session.status === "live") navigate(`/live-classes/${session.course_slug}/${session._id}/join`);
    else if (session.status === "ended" && session.recording_available) navigate(`/live-classes/${session.course_slug}/${session._id}/join`);
    else navigate(`/live-classes/${session.course_slug}/${session._id}/join`);
  };
 
  /* ── Derived ── */
  const savePct    = lc && lc.original_price > 0 ? Math.round((1 - lc.price / lc.original_price) * 100) : 0;
  const isLive     = lc?.status === "live";
  const isEnded    = lc?.status === "ended";
  const isSched    = lc?.status === "scheduled";
  const isCancelled= lc?.status === "cancelled";
  const isFree     = !!(lc?.is_free || lc?.price === 0);
  const enrolled   = lc?.enrolled_count || 0;
  const spotsLeft  = Math.max(0, (lc?.max_participants || 500) - enrolled);
 
  /* ── Build sessions list: current first, then siblings sorted by status then date ── */
  const sessionsList = useMemo((): ICourse[] => {
    if (!lc) return [];
    const rank = (s: ICourse) => s.status === "live" ? 0 : s.status === "scheduled" ? 1 : s.status === "ended" ? 2 : 3;
    return [lc, ...siblings].sort((a, b) => {
      const r = rank(a) - rank(b);
      if (r !== 0) return r;
      return new Date(a.scheduled_at || 0).getTime() - new Date(b.scheduled_at || 0).getTime();
    });
  }, [lc, siblings]);
 
  /* ── Loading / Error ── */
  if (loading) return <DetailSkeleton />;
  if (error || !lc) return (
    <div style={{ textAlign:"center", padding:80 }}>
      <h2>{error || "Class not found"}</h2>
      <Link to="/live-classes">← Back to Live Classes</Link>
    </div>
  );
 
  /* ══════════════════════════════════════════════
     RENDER — identical structure to CourseDetail
  ══════════════════════════════════════════════ */
  return (
    <>
      <Helmet>
        <title>{lc.title} – Live Class – Acadebility</title>
        <meta name="description" content={lc.description} />
      </Helmet>
 
      <div className="cd-page">
 
        {/* ── Back bar ── */}
        <div className="cd-back-bar">
          <Link to="/live-classes" className="cd-back-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
            </svg>
            Back to Live Classes
          </Link>
        </div>
 
        <div className="cd-layout">
 
          {/* ════ LEFT MAIN — identical structure to CourseDetail ════ */}
          <div className="cd-main">
 
            {/* Hero card */}
            <div className="cd-hero-card">
              <div className="cd-hero-badges">
                {isLive && (
                  <span className="cd-badge cd-badge-live" style={{ animation:"lcd-pulse-badge 1.5s infinite" }}>
                    <span className="lcd-live-dot" />LIVE NOW
                  </span>
                )}
                {isEnded && lc.recording_available && <span className="cd-badge cd-badge-recorded">⏺ Recording Ready</span>}
                {isFree  && <span className="cd-badge" style={{ background:"#10b981", color:"#fff" }}>FREE</span>}
                {lc.is_bestseller && <span className="cd-badge cd-badge-bestseller">🏆 Bestseller</span>}
                <span className="cd-badge cd-badge-cat">{lc.category}</span>
              </div>
 
              <h1 className="cd-hero-title">{lc.title}</h1>
              <p  className="cd-hero-desc">{lc.description}</p>
 
              <div className="cd-hero-meta">
                <span className="cd-meta-item cd-meta-rating">
                  <IcoStar />
                  <strong>{lc.rating}</strong>
                  <span className="cd-meta-rating-count">({(lc.review_count||0).toLocaleString("en-IN")} ratings)</span>
                </span>
                <span className="cd-meta-item"><IcoUsers /> {enrolled} enrolled</span>
                <span className="cd-meta-item"><IcoClock /> {lc.duration_minutes || 60} min / session</span>
              </div>
 
              <div className="cd-divider" />
 
              <div className="cd-instructor-label">Instructor</div>
              <div className="cd-instructor-row">
                <div className="cd-instructor-avatar">{lc.instructor_avatar}</div>
                <div>
                  <div className="cd-instructor-name">{lc.instructor}</div>
                  <div className="cd-instructor-title">{lc.instructor_title}</div>
                </div>
              </div>
            </div>
 
            {/* What You'll Learn */}
            <div className="cd-section-card">
              <h2 className="cd-section-title">What You'll Learn</h2>
              <div className="cd-learn-grid">
                {(lc.what_you_learn || []).map((item: string) => (
                  <div className="cd-learn-item" key={item}>
                    <span className="cd-learn-icon"><IcoCheck /></span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
 
            {/* ════════════════════════════════════════
                CLASS SCHEDULE — replaces Course Content
                Inspired by Unacademy / YouTube Premium
                schedule list. Each row = one session.
                Only LIVE rows have "Join Now".
            ════════════════════════════════════════ */}
            <div className="cd-section-card">
              <div className="cd-content-head">
                <h2 className="cd-section-title">📅 Class Schedule</h2>
                <span className="cd-content-meta">
                  {sessionsList.length} session{sessionsList.length !== 1 ? "s" : ""}
                </span>
              </div>
              <p className="lcd-schedule-hint">
                All sessions below are part of this live series. Enroll once to access all sessions and recordings.
              </p>
 
              <div className="lcd-sessions">
                {sessionsList.map((session, idx) => {
                  const sLive      = session.status === "live";
                  const sEnded     = session.status === "ended";
                  const sSched     = session.status === "scheduled";
                  const sCancelled = session.status === "cancelled";
                  const isCurrent  = session.slug === course_slug;
 
                  return (
                    <div
                      key={session.slug}
                      className={[
                        "lcd-session",
                        sLive      ? "lcd-session-live"      : "",
                        sEnded     ? "lcd-session-ended"     : "",
                        sCancelled ? "lcd-session-cancelled" : "",
                        isCurrent  ? "lcd-session-current"   : "",
                      ].join(" ").trim()}
                      onClick={() => !sCancelled && handleSessionClick(session)}
                      style={{ cursor: sCancelled ? "default" : "pointer" }}
                    >
                      {/* Left: status badge */}
                      <div className="lcd-session-badge">
                        {sLive ? (
                          <div className="lcd-sn-live"><span className="lcd-live-dot" /></div>
                        ) : sEnded ? (
                          <div className="lcd-sn-done">✓</div>
                        ) : sCancelled ? (
                          <div className="lcd-sn-cancel">✕</div>
                        ) : (
                          <div className="lcd-sn-num">{idx + 1}</div>
                        )}
                      </div>
 
                      {/* Middle: info */}
                      <div className="lcd-session-info">
                        <div className="lcd-session-title">
                          {session.title}
                          {isCurrent && <span className="lcd-current-tag">Viewing</span>}
                        </div>
                        <div className="lcd-session-meta">
                          {sLive && <span className="lcd-smeta lcd-smeta-live">🔴 In Progress · {session.enrolled_count || 0} joined</span>}
                          {sSched && session.scheduled_at && (
                            <span className="lcd-smeta lcd-smeta-sched">
                              📅 {fmtShort(session.scheduled_at)}&nbsp;&nbsp;<strong>{timeUntil(session.scheduled_at)}</strong>
                            </span>
                          )}
                          {sEnded && (
                            <span className="lcd-smeta lcd-smeta-ended">
                              {session.recording_available ? "⏺ Recording ready" : "✓ Completed"}
                            </span>
                          )}
                          {sCancelled && <span className="lcd-smeta lcd-smeta-cancel">Cancelled</span>}
                          <span className="lcd-smeta-dur"><IcoClock />{session.duration_minutes || 60} min</span>
                        </div>
                      </div>
 
                      {/* Right: per-session CTA */}
                      <div className="lcd-session-cta" onClick={e => e.stopPropagation()}>
                        {sCancelled ? (
                          <span className="lcd-sbtn lcd-sbtn-disabled">Cancelled</span>
                        ) : sEnded ? (
                          session.recording_available
                            ? isEnrolled
                              ? <button className="lcd-sbtn lcd-sbtn-rec" onClick={() => navigate(`/live-classes/${session.slug}/join`)}>▶ Watch</button>
                              : <button className="lcd-sbtn lcd-sbtn-lock" onClick={handleEnrollClick}>🔒 Unlock</button>
                            : <span className="lcd-sbtn lcd-sbtn-disabled">Processing</span>
                        ) : sLive ? (
                          isEnrolled
                            ? <button className="lcd-sbtn lcd-sbtn-join" onClick={() => navigate(`/live-classes/${session.course_slug}/${session._id}/join`)}>🔴 Join</button>
                            : <button className="lcd-sbtn lcd-sbtn-enroll-live" onClick={handleEnrollClick}>Enroll & Join</button>
                        ) : (
                          isEnrolled
                            ? <span className="lcd-sbtn lcd-sbtn-enrolled">✓ Enrolled</span>
                            : <button className="lcd-sbtn lcd-sbtn-enroll" onClick={handleEnrollClick}>Enroll</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
 
            {/* About this class */}
            <div className="cd-section-card">
              <h2 className="cd-section-title">📖 About this Class</h2>
              <p className="lcd-about-text">
                {lc.description} This session is designed to be highly interactive — raise your hand, unmute, and get your doubts cleared in real-time. Come prepared with your questions!
              </p>
              <div className="cd-learn-grid" style={{ marginTop:18 }}>
                {[
                  "Live interactive session","Real-time Q&A with instructor",
                  "Screen sharing & whiteboard","Recording available after class",
                  "Certificate of attendance","Direct access to instructor",
                ].map(f => (
                  <div className="cd-learn-item" key={f}>
                    <span className="cd-learn-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0d9e8a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </span>
                    <span>{f}</span>
                  </div>
                ))}
              </div>
              {/* Tags */}
              {(lc.tags || []).length > 0 && (
                <div className="lcd-tags-row">
                  {(lc.tags || []).map((t: string) => (
                    <span className="cd-lesson-preview" key={t}
                      style={{ background:"var(--cd-bg)", color:"var(--cd-text-mid)", border:"1.5px solid var(--cd-border)", fontSize:"0.78rem", padding:"4px 12px", borderRadius:20 }}>
                      {t}
                    </span>
                  ))}
                  <span className="cd-lesson-preview"
                    style={{ background:"var(--cd-bg)", color:"var(--cd-text-mid)", border:"1.5px solid var(--cd-border)", fontSize:"0.78rem", padding:"4px 12px", borderRadius:20 }}>
                    Live Q&A
                  </span>
                </div>
              )}
            </div>
 
            {/* Instructor bio — identical to CourseDetail */}
            <div className="cd-section-card">
              <h2 className="cd-section-title">About the Instructor</h2>
              <div className="cd-instr-bio-row">
                <div className="cd-instr-bio-avatar">{lc.instructor_avatar}</div>
                <div>
                  <div className="cd-instr-bio-name">{lc.instructor}</div>
                  <div className="cd-instr-bio-title">{lc.instructor_title}</div>
                  <div className="lcd-instr-stats">
                    <span>⭐ {lc.rating} Rating</span>
                    <span>👥 {lc.students_display} Students</span>
                    <span>🎓 50+ Classes Taught</span>
                  </div>
                  {lc.instructor_bio && <p className="cd-instr-bio-text">{lc.instructor_bio}</p>}
                </div>
              </div>
            </div>
          </div>
 
          {/* ════ RIGHT SIDEBAR — identical card structure to CourseDetail ════ */}
          <aside className="cd-sidebar">
            <div className="cd-enroll-card">
 
              {/* Thumbnail area — replaces CourseDetail's video preview */}
              <div className="cd-preview-wrap" style={{ cursor: isLive && isEnrolled ? "pointer" : "default" }}
                onClick={() => isLive && isEnrolled && navigate(`/live-classes/${lc.slug}/join`)}>
                <img src={lc.image_url} alt={lc.title} className="cd-preview-img" />
 
                {/* Overlay: LIVE pulsing */}
                {isLive && (
                  <div className="lcd-live-overlay">
                    <span className="lcd-live-dot" />
                    <span>LIVE NOW · {enrolled} watching</span>
                  </div>
                )}
 
                {/* Overlay: Countdown for scheduled */}
                {isSched && lc.scheduled_at && (
                  <div className="lcd-sched-overlay">
                    <div className="lcd-sched-label">Starts in</div>
                    <Countdown target={lc.scheduled_at} />
                  </div>
                )}
 
                {/* Overlay: Recording for ended */}
                {isEnded && lc.recording_available && (
                  <div className="lcd-rec-overlay">
                    <span>⏺ Recording Available</span>
                  </div>
                )}
 
                {/* "Preview this class" label (shown only if no other overlay) */}
                {!isLive && !isSched && !isEnded && (
                  <div className="cd-preview-label">View class details</div>
                )}
              </div>
 
              {/* LIVE indicator box (below thumbnail, like "Class is LIVE!") */}
              {isLive && (
                <div className="lcd-live-now-box">
                  <span className="lcd-live-dot" />
                  <div>
                    <strong>Class is LIVE right now!</strong>
                    <div style={{ fontSize:"0.78rem", marginTop:2 }}>{enrolled} people in the room</div>
                  </div>
                </div>
              )}
 
              {/* Spots warning */}
              {!isEnded && !isCancelled && spotsLeft > 0 && spotsLeft < 50 && (
                <div className="lcd-spots-warn">🔥 Only {spotsLeft} spots left!</div>
              )}
              {!isEnded && !isCancelled && spotsLeft === 0 && (
                <div className="lcd-spots-full">⚠️ Class is Full</div>
              )}
 
              {/* Price — same as CourseDetail */}
              {!isFree && (
                <div className="cd-price-section">
                  <div className="cd-price-row">
                    <span className="cd-price-now">₹{lc.price.toLocaleString("en-IN")}</span>
                    {lc.original_price > 0 && <span className="cd-price-orig">₹{lc.original_price.toLocaleString("en-IN")}</span>}
                  </div>
                  {savePct > 0 && <div className="cd-price-save">🔥 Save {savePct}% · Limited Time</div>}
                </div>
              )}
 
              {/* ── Main CTA button — same style as CourseDetail ── */}
              <button
                className={[
                  "cd-enroll-btn",
                  isEnrolled && !isLive  ? "cd-enroll-btn-enrolled" : "",
                  isLive && !isEnrolled  ? "lcd-btn-live"           : "",
                  isLive &&  isEnrolled  ? "lcd-btn-join"           : "",
                  isCancelled            ? "lcd-btn-cancelled"      : "",
                ].join(" ").trim()}
                onClick={handleEnrollClick}
                disabled={isCancelled || (isEnded && !lc.recording_available)}
              >
                {isCancelled ? "Class Cancelled"
                  : isEnded
                    ? lc.recording_available
                      ? isEnrolled ? "▶ Watch Recording" : `Unlock Recording${!isFree ? ` — ₹${lc.price.toLocaleString("en-IN")}` : ""}`
                      : "Recording processing…"
                    : isLive
                      ? isEnrolled
                        ? "🔴 Join Live Class Now"
                        : `Enroll & Join Live${!isFree ? ` — ₹${lc.price.toLocaleString("en-IN")}` : " — Free"}`
                      : isEnrolled
                        ? "✓ You're Enrolled"
                        : isFree
                          ? "Reserve Your Spot — Free"
                          : `Enroll Now — ₹${lc.price.toLocaleString("en-IN")}`
                }
              </button>
 
              {!isEnrolled && (
                <p className="cd-enroll-hint">🔐 Login required · Free cancellation before class</p>
              )}
 
              <div className="cd-guarantee"><IcoShield /> Free cancellation up to 1 hr before class</div>
              <div className="cd-includes-divider" />
              <div className="cd-includes-title">This class includes:</div>
 
              {/* Includes grid — same cd-inc-tile as CourseDetail */}
              <div className="cd-includes-grid">
                {[
                  { icon:<IcoVideoFill />, label:"Live interactive session",    color:"#0d9e8a", bg:"#e6faf7" },
                  { icon:<IcoClock />,     label:`${lc.duration_minutes||60} min / session`, color:"#6366f1", bg:"#eef2ff" },
                  { icon:<IcoAward />,     label:"Certificate of attendance",   color:"#f59e0b", bg:"#fffbeb" },
                  { icon:<IcoCalendar />,  label:`${sessionsList.length} session${sessionsList.length!==1?"s":""}`, color:"#3b82f6", bg:"#eff6ff" },
                  { icon:<IcoMic />,       label:"Real-time Q&A",               color:"#ec4899", bg:"#fdf2f8" },
                  { icon:<IcoZap />,       label:"Recording after class",        color:"#8b5cf6", bg:"#faf5ff" },
                ].map(item => (
                  <div className="cd-inc-tile" key={item.label}>
                    <div className="cd-inc-icon" style={{ color:item.color, background:item.bg }}>{item.icon}</div>
                    <span className="cd-inc-label">{item.label}</span>
                  </div>
                ))}
              </div>
 
              {/* Info list */}
              <div className="lcd-info-list">
                {lc.scheduled_at && <div className="lcd-info-row"><span>📅</span><span><strong>{isLive ? "Started:" : "Next:"}</strong> {fmtFull(lc.scheduled_at)}</span></div>}
                <div className="lcd-info-row"><span>⏱</span><span><strong>Duration:</strong> {lc.duration_minutes || 60} minutes</span></div>
                <div className="lcd-info-row"><span>🎓</span><span><strong>Platform:</strong> Zoom (embedded)</span></div>
                <div className="lcd-info-row"><span>💬</span><span><strong>Language:</strong> Hindi + English</span></div>
                <div className="lcd-info-row"><span>🔐</span><span><strong>Access:</strong> Enrolled students only</span></div>
              </div>
 
              {/* Share row — identical to CourseDetail */}
              <div className="cd-share-row">
                <button className="cd-share-btn">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                  Share
                </button>
                <button className="cd-wish-btn">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                  Wishlist
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>
 
      {/* LoginModal — same as TestSeriesNormal / CourseDetail */}
      {showLoginModal && (
        <LoginModal
          show={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          onLoginSuccess={handleLoginSuccess}
        />
      )}
 
      {/* TestPriceModal — same as TestSeriesNormal / CourseDetail */}
      {showPricingModal && (
        <TestPriceModal
          show={showPricingModal}
          onClose={() => setShowPricingModal(false)}
          discounted_price={String(lc.price)}
          original_price={String(lc.original_price || lc.price)}
          blogTitle={lc.title}
          test_category_name={lc.category}
          onCheckout={() => {
            setShowPricingModal(false);
            setIsEnrolled(true);
            // If class is currently LIVE → go to classroom immediately after payment
            if (lc.status === "live") navigate(`/live-classes/${lc.slug}/join`);
          }}
        />
      )}
    </>
  );
};
 
export default LiveClassDetail;